import React, { useState, useEffect } from 'react';
import { Shield, Key, Clock, CheckCircle, XCircle, Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const MyVerification = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    scope: [],
    expiresInDays: 30,
    maxUses: '',
    description: ''
  });

  const scopeOptions = [
    { value: 'age_over_18', label: 'Age Over 18', description: 'Confirm user is 18 or older' },
    { value: 'age_over_21', label: 'Age Over 21', description: 'Confirm user is 21 or older' },
    { value: 'date_of_birth', label: 'Date of Birth', description: 'Share exact date of birth' },
    { value: 'full_name', label: 'Full Name', description: 'Share full legal name' },
    { value: 'address', label: 'Address', description: 'Share current address' },
    { value: 'email', label: 'Email Address', description: 'Share email address' },
    { value: 'phone', label: 'Phone Number', description: 'Share phone number' },
    { value: 'nationality', label: 'Nationality', description: 'Share nationality' },
    { value: 'document_number', label: 'Document Number', description: 'Share ID document number' },
    { value: 'all_verified_data', label: 'All Verified Data', description: 'Share all verified information' }
  ];

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      const response = await api.get('/sharing-tokens');
      setTokens(response.data.tokens || []);
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
      toast.error('Failed to load sharing tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleScopeToggle = (scopeValue) => {
    setFormData(prev => ({
      ...prev,
      scope: prev.scope.includes(scopeValue)
        ? prev.scope.filter(s => s !== scopeValue)
        : [...prev.scope, scopeValue]
    }));
  };

  const handleCreateToken = async (e) => {
    e.preventDefault();
    
    if (formData.scope.length === 0) {
      toast.error('Please select at least one data scope');
      return;
    }

    try {
      const payload = {
        scope: formData.scope,
        expiresInDays: parseInt(formData.expiresInDays),
        description: formData.description || 'Sharing token'
      };

      if (formData.maxUses) {
        payload.maxUses = parseInt(formData.maxUses);
      }

      const response = await api.post('/sharing-tokens', payload);
      toast.success('Sharing token created successfully!');
      setShowCreateForm(false);
      setFormData({ scope: [], expiresInDays: 30, maxUses: '', description: '' });
      fetchTokens();
    } catch (error) {
      console.error('Failed to create token:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to create token');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const isTokenValid = (token) => {
    if (token.revoked_at) return false;
    if (new Date(token.expires_at) < new Date()) return false;
    if (token.max_uses && token.use_count >= token.max_uses) return false;
    return true;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Verification</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage your verified data and control who can access it through sharing tokens.
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-blue-900 mb-1">Your Data, Your Control</h3>
            <p className="text-sm text-blue-800 mb-2">
              Generate secure sharing tokens to share your verified data with institutions.
              Choose exactly what information to share, set expiration dates, and revoke access anytime.
            </p>
            <ul className="text-sm text-blue-800 space-y-0.5">
              <li>• Share only what's needed (e.g., "Over 18" instead of full date of birth)</li>
              <li>• Set expiration dates and usage limits</li>
              <li>• Track who accessed your data and when</li>
              <li>• Revoke tokens instantly if needed</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Create Token Button */}
      <div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Generate Sharing Token
        </button>
      </div>

      {/* Create Token Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Sharing Token</h3>
          <form onSubmit={handleCreateToken} className="space-y-4">
            {/* Scope Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Data to Share *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {scopeOptions.map(option => (
                  <div
                    key={option.value}
                    className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.scope.includes(option.value)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleScopeToggle(option.value)}
                  >
                    <input
                      type="checkbox"
                      checked={formData.scope.includes(option.value)}
                      onChange={() => handleScopeToggle(option.value)}
                      className="mt-0.5 mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 text-sm">{option.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{option.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Expiration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expires In (Days) *
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={formData.expiresInDays}
                onChange={(e) => setFormData({ ...formData, expiresInDays: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                required
              />
            </div>

            {/* Max Uses */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Uses (Optional)
              </label>
              <input
                type="number"
                min="1"
                value={formData.maxUses}
                onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                placeholder="Unlimited"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., For bank account verification"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Create Token
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tokens List */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">Your Sharing Tokens</h3>
        </div>
        <div>
          {tokens.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Key className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No sharing tokens yet</p>
              <p className="text-sm text-gray-500 mt-1">
                Create a token to share your verified data with institutions
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {tokens.map(token => (
                <div key={token.id} className="px-6 py-4">
                  <div className="flex items-start gap-3 mb-3">
                    {isTokenValid(token) ? (
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="font-medium text-gray-900">
                          {token.metadata?.description || 'Sharing Token'}
                        </span>
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          isTokenValid(token)
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {isTokenValid(token) ? 'Active' : 'Expired'}
                        </span>
                      </div>

                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="break-all">
                          <span className="font-medium">Token:</span>{' '}
                          <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                            {token.token}
                          </code>
                        </div>
                        <div>
                          <span className="font-medium">Scope:</span> {token.scope.join(', ')}
                        </div>
                        <div className="flex items-center gap-4 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Expires: {formatDate(token.expires_at)}
                          </span>
                          {token.max_uses && (
                            <span>
                              Uses: {token.use_count} / {token.max_uses}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyVerification;

